import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const DONE_MS = 1600;
const ERROR_MS = 3200;

let liveRegion = null;

function announce(message) {
  if (!message) return;
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  window.requestAnimationFrame(() => {
    if (liveRegion) liveRegion.textContent = message;
  });
}

const routes = () => {
  const themeRoutes = window.Theme?.routes || {};
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  return {
    add: themeRoutes.cart_add_url || `${root}cart/add.js`,
    cart: themeRoutes.cart_url || `${root}cart`,
  };
};

const cartSectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);

async function readCart() {
  const component = document.querySelector('cart-items-component');
  if (component && typeof component.fetchCartData === 'function') return component.fetchCartData();
  const response = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart ${response.status}`);
  return response.json();
}

/**
 * Adds variants to the cart and announces it with Horizon's cart event, so the cart drawer and the cart count
 * update and the drawer opens, exactly as after a product form submit.
 *
 * @param {Array<{ id: number|string, quantity?: number, properties?: object }>} items
 * @param {{ source?: EventTarget, productId?: string, origin?: string }} [options]
 * @returns {Promise<object>} the cart. Rejects with an Error; `handled` is true when the shop refused the lines
 * (its message is meant for the shopper), false for network errors.
 */
export async function addToCart(items, { source = document, productId = '', origin = 'lucid-card' } = {}) {
  const lines = items.map((item) => ({ id: Number(item.id), quantity: item.quantity || 1, ...(item.properties ? { properties: item.properties } : {}) }));
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(routes().add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines,
        ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
      }),
    });
    const data = await response.json();
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

    if (!response.ok || data.status) {
      source.dispatchEvent(
        new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } })
      );
      const cart = await readCart().catch(() => null);
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: { didError: true, items: cart?.items || [], source: origin, itemCount, productId },
      });
      const error = new Error(data.description || data.message || 'Add to cart failed');
      error.handled = true;
      throw error;
    }

    const cart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: origin, itemCount, productId, sections: data.sections },
    });
    return cart;
  } catch (error) {
    if (!error.handled) {
      deferred.reject(error);
      source.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    }
    throw error;
  }
}

let QuickAddElement = null;
let sharedTrigger = null;

let sheetModule = null;

function quickSheetModule() {
  const url = document.querySelector('[data-lucid-quick-body][data-module]')?.getAttribute('data-module');
  if (!url) return null;
  sheetModule ??= import(url);
  return sheetModule;
}

/** Starts loading the quick add sheet for a product before the tap ends. */
export function prefetchQuickAdd(url) {
  quickSheetModule()
    ?.then((module) => module.prefetchQuickSheet(url))
    .catch(() => {});
}

/**
 * Opens the Lucid quick add sheet (a bottom sheet on phones, a side drawer from 750px). Falls back to Horizon's quick
 * add dialog through one shared trigger, so cards do not each keep a component with its own document listeners.
 * Resolves false when neither is on the page.
 */
export async function openQuickAdd({ url, title }) {
  const sheet = quickSheetModule();
  if (sheet) {
    const module = await sheet;
    if (await module.openQuickSheet({ url, title, addToCart, announce })) return true;
  }
  if (!document.getElementById('quick-add-dialog')) return false;
  if (!QuickAddElement) {
    const { QuickAddComponent } = await import('@theme/quick-add');
    QuickAddElement =
      customElements.get('lucid-quick-add') ||
      class LucidQuickAdd extends QuickAddComponent {
        get productPageUrl() {
          return this.dataset.productUrl || '';
        }
      };
    if (!customElements.get('lucid-quick-add')) customElements.define('lucid-quick-add', QuickAddElement);
  }
  if (!sharedTrigger?.isConnected) {
    sharedTrigger = document.createElement('lucid-quick-add');
    sharedTrigger.hidden = true;
    document.body.append(sharedTrigger);
  }
  const trigger = sharedTrigger;
  trigger.dataset.productUrl = url;
  trigger.dataset.productTitle = title;
  await trigger.handleClick(new Event('click', { cancelable: true }));
  return true;
}

const TRANSITION_TYPE = 'product-image-transition';
const preloaded = new Map();

/** Loads and decodes a colour image at the size the card shows it, once per srcset. */
function preload(srcset, src, sizes) {
  if (!srcset && !src) return Promise.resolve(null);
  const key = srcset || src;
  if (!preloaded.has(key)) {
    const image = new Image();
    image.decoding = 'async';
    if (sizes) image.sizes = sizes;
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
    preloaded.set(
      key,
      image.decode().then(
        () => image,
        () => image
      )
    );
  }
  return preloaded.get(key);
}

function clearTransitionMarks() {
  for (const element of document.querySelectorAll('lucid-card [data-view-transition-triggered]')) {
    element.removeAttribute('data-view-transition-type');
    element.removeAttribute('data-view-transition-triggered');
  }
}

/* Back from the product page restores this page from the cache: no stale morph marks, no previewed colours. */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  clearTransitionMarks();
  for (const card of document.querySelectorAll('lucid-card')) card.resetColour?.();
});

const isPlainClick = (event) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;

class LucidCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.addEventListener('pointerover', this.#onPointerOver, { passive: true });
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.querySelector('.lucid-card__dots')?.addEventListener('pointerleave', this.#onDotsLeave, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.querySelector('.lucid-card__dots')?.removeEventListener('pointerleave', this.#onDotsLeave);
  }

  #onPointerDown = (event) => {
    if (this.dataset.quick !== 'modal') return;
    if (event.target instanceof Element && event.target.closest('[data-lucid-card-add]')) prefetchQuickAdd(this.dataset.url);
  };

  /* Colour dots: preload on the first touch, preview on hover and keyboard focus, show on tap before navigating. */

  #dotOf(target) {
    const dot = target instanceof Element ? target.closest('.lucid-card__dot') : null;
    return dot instanceof HTMLElement && this.contains(dot) ? dot : null;
  }

  /** The image layer the shopper sees right now (product or model shot). */
  #visibleLayer() {
    const layers = Array.from(this.querySelectorAll('.lucid-card__media .lucid-card__layer'));
    return (
      layers.find((layer) => {
        const style = window.getComputedStyle(layer);
        return style.display !== 'none' && Number(style.opacity) > 0.5;
      }) || layers[0]
    );
  }

  #sourceFor(dot, layer) {
    const model = layer?.classList.contains('lucid-card__layer--model');
    if (model && (dot.dataset.swapModelSrcset || dot.dataset.swapModelSrc)) {
      return { srcset: dot.dataset.swapModelSrcset, src: dot.dataset.swapModelSrc, fit: '' };
    }
    return { srcset: dot.dataset.swapSrcset, src: dot.dataset.swapSrc, fit: model ? 'contain' : '' };
  }

  #preloadDot(dot) {
    const layer = this.#visibleLayer();
    const { srcset, src } = this.#sourceFor(dot, layer);
    return preload(srcset, src, layer?.querySelector('img')?.sizes);
  }

  #onPointerOver = (event) => {
    const dot = this.#dotOf(event.target);
    if (!dot) return;
    if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
      if (dot.classList.contains('is-current')) this.resetColour();
      else this.#showColour(dot);
    } else {
      this.#preloadDot(dot);
    }
  };

  #onTouchStart = (event) => {
    const dot = this.#dotOf(event.target);
    if (dot && !dot.classList.contains('is-current')) this.#preloadDot(dot);
  };

  #onDotsLeave = (event) => {
    if (event.pointerType === 'mouse' || event.pointerType === 'pen') this.resetColour();
  };

  #onFocusIn = (event) => {
    const dot = this.#dotOf(event.target);
    if (!dot || !event.target.matches(':focus-visible')) return;
    if (dot.classList.contains('is-current')) this.resetColour();
    else this.#showColour(dot);
  };

  #onFocusOut = (event) => {
    if (!this.#dotOf(event.target)) return;
    if (this.#dotOf(event.relatedTarget)) return;
    this.resetColour();
  };

  #shown = null;
  #original = null;

  async #showColour(dot) {
    if (this.#shown === dot) return;
    const layer = this.#visibleLayer();
    const { srcset, src, fit } = this.#sourceFor(dot, layer);
    if (!layer || (!srcset && !src)) return;
    this.#shown = dot;

    const title = this.querySelector('.lucid-card__title-link');
    const price = this.querySelector('.lucid-card__info .lucid-card__price');
    this.#original ??= { title: title?.textContent ?? '', price: price?.outerHTML ?? '' };
    if (title && dot.dataset.swapTitle) title.textContent = dot.dataset.swapTitle;
    if (price) this.#setPrice(dot.dataset.swapPrice || this.#original.price);

    const base = layer.querySelector('img:not(.lucid-card__swap)');
    const image = await preload(srcset, src, base?.sizes);
    if (this.#shown !== dot || !image) return;

    let overlay = layer.querySelector('.lucid-card__swap');
    if (!(overlay instanceof HTMLImageElement)) {
      overlay = document.createElement('img');
      overlay.className = 'lucid-media__img lucid-card__swap';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.decoding = 'async';
      layer.append(overlay);
      overlay.getBoundingClientRect();
    }
    overlay.style.objectFit = fit;
    if (base?.sizes) overlay.sizes = base.sizes;
    if (srcset) overlay.srcset = srcset;
    if (src) overlay.src = src;
    if (overlay.width === 0 && base) {
      overlay.width = base.width;
      overlay.height = base.height;
    }
    overlay.classList.add('is-shown');
  }

  /** Back to the card's own colour. Public so the page can reset cards restored from the back/forward cache. */
  resetColour() {
    if (!this.#shown) return;
    this.#shown = null;
    for (const overlay of this.querySelectorAll('.lucid-card__swap')) overlay.classList.remove('is-shown');
    if (!this.#original) return;
    const title = this.querySelector('.lucid-card__title-link');
    if (title) title.textContent = this.#original.title;
    this.#setPrice(this.#original.price);
  }

  #setPrice(html) {
    const price = this.querySelector('.lucid-card__info .lucid-card__price');
    if (!price || !html || price.outerHTML === html) return;
    const template = document.createElement('template');
    template.innerHTML = html;
    const next = template.content.firstElementChild;
    if (next) price.replaceWith(next);
  }

  /* Marks the visible image so view-transitions.js morphs it into the first image of the product gallery. */
  #markTransition() {
    const layer = this.#visibleLayer();
    if (!layer) return;
    clearTransitionMarks();
    layer.setAttribute('data-view-transition-type', TRANSITION_TYPE);
    layer.setAttribute('data-view-transition-triggered', 'true');
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-lucid-bis-open]')) return;
    const size = event.target.closest('.lucid-card__size');
    if (size instanceof HTMLButtonElement && this.contains(size)) {
      event.preventDefault();
      this.#add(size, size.dataset.variantId);
      return;
    }
    const trigger = event.target.closest('[data-lucid-card-add]');
    if (trigger instanceof HTMLElement && this.contains(trigger)) {
      event.preventDefault();
      if (this.dataset.quick === 'direct') {
        this.#add(trigger, this.dataset.variantId);
      } else {
        this.#openModal(trigger);
      }
      return;
    }
    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link) || !isPlainClick(event)) return;
    if (link.target && link.target !== '_self') return;
    const dot = this.#dotOf(link);
    if (dot && !dot.classList.contains('is-current')) this.#showColour(dot);
    this.#markTransition();
  };

  async #openModal(trigger) {
    trigger.setAttribute('aria-busy', 'true');
    try {
      const opened = await openQuickAdd({ url: this.dataset.url, title: this.dataset.title || '' });
      if (!opened) window.location.href = this.dataset.url;
    } catch {
      window.location.href = this.dataset.url;
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }

  async #add(trigger, variantId) {
    if (!variantId || trigger.getAttribute('aria-busy') === 'true') return;
    const quick = this.querySelector('.lucid-card__quick');
    trigger.setAttribute('aria-busy', 'true');
    try {
      await addToCart([{ id: variantId, quantity: 1 }], { source: this, productId: this.dataset.productId });
      announce(this.dataset.addedLabel || window.Theme?.translations?.added);
      quick?.classList.add('is-done');
      window.setTimeout(() => quick?.classList.remove('is-done'), DONE_MS);
    } catch (error) {
      this.#showError(error?.handled && error.message ? error.message : this.dataset.errorLabel || '');
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }

  #toastTimer = 0;

  #showError(message) {
    if (!message) return;
    const media = this.querySelector('.lucid-card__media');
    if (!media) {
      announce(message);
      return;
    }
    let toast = media.querySelector('.lucid-card__toast');
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'lucid-card__toast lucid-glass lucid-r-control';
      toast.setAttribute('role', 'status');
      media.append(toast);
    }
    toast.textContent = message;
    this.querySelector('.lucid-card__quick')?.classList.add('is-error');
    window.clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => {
      toast.remove();
      this.querySelector('.lucid-card__quick')?.classList.remove('is-error');
    }, ERROR_MS);
  }
}

if (!customElements.get('lucid-card')) customElements.define('lucid-card', LucidCard);
